import { cloneElement, isValidElement } from "react";
import type { ReactNode, Ref, MouseEventHandler } from "react";
import { useDelayedTooltip } from "@/hooks/useDelayedTooltip";

interface HelpHintProps {
  /** Explanation shown in the bubble. With no message the child renders
   *  untouched (no hover behaviour, no help cursor). */
  message?: string;
  /**
   * How long the pointer must rest on the label before the bubble appears.
   * Defaults to the "hover a bit longer to learn what this does" feel from the
   * guest-detail popup — deliberately not instant, so it never flashes while
   * the pointer sweeps across a column of labels.
   */
  delay?: number;
  /**
   * The single label / heading element the hint explains. The help cursor, the
   * hover handlers and the measuring ref are attached DIRECTLY to this element
   * (via cloneElement) — there is no wrapper box. That's the whole point: a
   * wrapper `<span>` gets its own line box that can sit a few px above the
   * glyphs, so the `cursor-help` region floated above the text. Cloning onto
   * the real element means the question-mark cursor lands exactly on the text.
   */
  children: ReactNode;
}

function setRef<T>(ref: Ref<T> | undefined, value: T | null) {
  if (typeof ref === "function") ref(value);
  else if (ref && typeof ref === "object")
    (ref as { current: T | null }).current = value;
}

function chain<E>(
  ...handlers: (MouseEventHandler<E> | undefined)[]
): MouseEventHandler<E> {
  return (event) => handlers.forEach((h) => h?.(event));
}

/**
 * Attaches a delayed help tooltip + the `cursor-help` (question-mark) cursor to
 * its child element so that resting the pointer on the label text itself (no
 * separate info icon) reveals an explanation after `delay` ms. Reuses
 * {@link useDelayedTooltip}: the bubble is portalled to `document.body` and
 * anchored just below the trigger.
 */
export const HelpHint: React.FC<HelpHintProps> = ({
  message,
  delay = 600,
  children,
}) => {
  const { ref, onMouseEnter, onMouseLeave, tooltip } =
    useDelayedTooltip<HTMLElement>(message, delay);

  if (!message || !isValidElement(children)) return <>{children}</>;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const child = children as React.ReactElement<any>;
  // React 19 exposes the child's ref on props; keep a fallback for safety.
  const existingRef: Ref<HTMLElement> | undefined =
    child.props.ref ?? (child as { ref?: Ref<HTMLElement> }).ref;

  const merged = {
    // Merge our measuring ref with whatever ref the child already carries
    // (e.g. FormInput's labelRef, PictureInput's pRef).
    ref: (node: HTMLElement | null) => {
      setRef(ref, node);
      setRef(existingRef, node);
    },
    className: `${child.props.className ?? ""} cursor-help`,
    onMouseEnter: chain(child.props.onMouseEnter, onMouseEnter),
    onMouseLeave: chain(child.props.onMouseLeave, onMouseLeave),
  };

  return (
    <>
      {cloneElement(child, merged)}
      {tooltip}
    </>
  );
};
